<template>
  <div :id="id" :class="['card', { 'card--no-header': !header }, styleClass, cardGridClasses, $attrs['class']]" :style="style">
    <div v-if="header"
         :class="[guideClass, headerStyleClass, { 'collapsed': isCollapsed}]"
         :data-toggle="isCollapsible ? 'collapse' : 'none'"
         class="card__header"
         @click="toggleCollapse">
      <div class="card__header__title">
        <h1 v-html="title"></h1>
      </div>
      <div :class="{'card__header__right--has-button': hasButton}" class="card__header__right">
        <div class="card__header__right__content">
          <slot name="header-right" />
        </div>
        <div v-if="cardType !== 'default' || !isNil($slots['header-button'])" class="card__header__right__button">
          <span @click="preventToggleCard"><slot name="header-button" /></span>
          <div v-if="cardType === 'record'" class="card__btn card__btn__delete" @click="handleClickDelete($event)">
            <icon icon="close" />
          </div>
          <div v-if="cardType === 'collapsible'" :class="{'card__btn__collapse--collapsed': !isCollapsed}" class="card__btn card__btn__collapse">
            <icon icon="collapse" />
          </div>
        </div>
      </div>
    </div>
    <transition-collapse>
      <div v-if="!isCollapsed"
           :class="[contentStyleClass, {'collapsed': isCollapsed}, {'card__content--max-height': !isNil(maxHeight) }, {'card__content--clickable': contentClickable}]"
           :style="cardContentStyle"
           :data-parentid="parentId"
           :data-toggle="isCollapsible ? 'collapse' : 'none'"
           class="card__content"
           style="padding:0!important;"
           @click="clickContent">
        <div :class="[{'card__content__inner--center': center }]" class="card__content__inner">
          <div v-if="disabled" class="card__overlay__disabled" /> <!-- it's important that this is BEFORE the slot - otherwise the CSS :last-child query is wrong and adds a divider to the very end of the card -->
          <slot />
        </div>
      </div>
    </transition-collapse>
  </div>
</template>

<script>

import GridMixin from '@/mixins/GridMixin';
import { isNil, isEmpty, sampleSize } from 'lodash';

export default {
  name: 'Card',

  mixins: [GridMixin],

  props: {
    title: String,
    msg: 'Swag',
    cardType: {
      type: String,
      default: 'default',
      validator: val => ['default', 'collapsible', 'record'].includes(val),
    },
    styleClass: String,
    headerStyleClass: String,
    contentStyleClass: String,
    guide: {
      type: String,
      default: 'mint',
      validator: val => ['mint', 'orange', 'none'].includes(val),
    },
    parentId: String,
    cardContentStyle: String,
    cols: {
      default: 4,
    },
    maxHeight: {
      type: String,
      required: false,
      default: null,
    },
    minHeight: {
      type: String,
      required: false,
      default: null,
    },
    contentClickable: {
      required: false,
      type: Boolean,
      default: false,
    },
    center: {
      required: false,
      type: Boolean,
      default: false,
    },
    collapsed: {
      required: false,
      type: Boolean,
      default: true,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
    header: {
      required: false,
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isCollapsed: false,
    };
  },

  computed: {
    id: function () {
      const id = this.$attrs.id || `comp_${this._uid}`;
      return isEmpty(this._parentHtmlId) ? id : `${this._parentHtmlId}-${id}`;
    },
    _parentHtmlId: function () {
      let it = this.$parent;
      while (!isNil(it)) {
        if (!isEmpty(it.id)) {
          return it.id;
        }
        it = it.$parent;
      }
      return '';
    },
    style: function () {
      if (isEmpty(this.maxHeight) && isEmpty(this.minHeight)) return {};

      if (isEmpty(this.maxHeight) && !isEmpty(this.minHeight)) return { minHeight: this.minHeight };

      return {
        minHeight: this.minHeight,
        maxHeight: this.maxHeight,
        overflow: 'hidden',
      };
    },
    isCollapsible: function () {
      return this.cardType === 'collapsible';
    },
    guideClass: function () {
      if (this.isCollapsible) {
        return 'card__header__guide card__header__guide--orange';
      }
      switch (this.guide) {
        case 'orange':
          return 'card__header__guide card__header__guide--orange';
        case 'none':
          return '';
        case 'mint':
        default:
          return 'card__header__guide card__header__guide--mint';
      }
    },
    hasButton: function () {
      return this.cardType !== 'default' || !isNil(this.$slots['header-button']);
    },
    cardGridClasses: function () {
      if (this.isCollapsible) {
        return 'col-12';
      } else {
        return this.gridClasses;
      }
    },
  },

  watch: {
    collapsed: function (newVal) {
      this.isCollapsed = newVal;
    },
  },

  created: function () {
    if (!this.isCollapsible) {
      this.isCollapsed = false;
    } else {
      this.isCollapsed = this.collapsed;
    }
  },

  methods: {
    handleClickDelete: function (event) {
      this.$emit('delete', event);
    },
    clickContent: function ($event) {
      if (this.contentClickable === true) {
        this.$emit('clickContent', $event);
      }
    },
    isNil: function (test) {
      return isNil(test);
    },
    toggleCollapse: function () {
      if (this.isCollapsible) {
        this.isCollapsed = !this.isCollapsed;
      }
      this.$emit('collapse', this.isCollapsed);
    },
    preventToggleCard: function (event) {
      event.stopPropagation();
    },
  },
};
</script>


<style lang="scss">
  @import '~styles/components/lib/Card';
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  @import '~styles/import';

  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }

  .card__content {
    display: block;
  }

  .card {
    &--info {
      .card {
        &__header {
          background-color: $red;
          color: $white;
          &__guide:before {
            background-color: transparent;
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/lib/layout/Card.vue